import React, { useState } from 'react';
import { Monitor, Sparkles } from 'lucide-react';
import { getIcon } from '../services/iconRegistry';
import { resolveAccent, tint } from '../utils/accentColor';
import ShimmerImage from './ShimmerImage';
import { dealCardTotals, buildPricePhases, phaseHeadline, phaseDisclosure } from '@platform/shared-types';
import type { PhasedLine } from '@platform/shared-types';
import type { DealPreviewDevice, DealPreviewOption, DealPreviewModel } from './dealPreviewModel';
import { money } from './finance/financeFormat';

/** Same wording the preview model uses for its own labels. */
const formatDollars = (cents: number) => money(cents);

/**
 * Mirrors platform-app RubytillLandingPage pricing card + SignupAccountFirst
 * deal banner — keep visually in sync; not imported because the apps are
 * separate repos.
 *
 * VISUAL AUTHORITY: `applications/platform-app/mockups/deal-editor-live-preview.html`.
 * Its `.price-card`, `.dev-strip` and `.deal-banner` blocks are
 * reproduced here colour-for-colour with Tailwind arbitrary values — every
 * hex, rgba and pixel measurement below is copied from that file's <style>
 * block, not approximated. If the two ever disagree, the mockup is right and
 * this file is the one that is stale.
 *
 * Renders straight off `DealPreviewModel` (`dealPreviewModel.ts`) and nothing
 * else — this component has no arithmetic of its own to get wrong.
 */

type PreviewTab = 'card' | 'banner';

const RUBY = '#DC143C';
const RUBY_DEEP = '#9F1239';
/**
 * THE SAVING COLOUR — amber. Used for every price the deal actually moved, and
 * for nothing else, so a struck-through list price always points at a figure in
 * this colour and a card with no colour on it is a card at list.
 *
 * Deliberately NOT ruby: ruby is the brand and the limited-time badge, and a
 * discounted price in the brand colour is indistinguishable from a price that
 * merely happens to be on a Ruby card. Deliberately not green either — green on
 * a dark card reads as a status, not as money.
 */
const SAVING = '#fbbf24';
/** The disclosure ink: legible on #101013 without shouting like the badge. */
const RUBY_LIGHT = '#fb7185';

/**
 * The disclosure with its OPENING clause removed, for a surface whose headline
 * already states that clause in 46px type.
 *
 * `phaseDisclosure` writes "$5.00/month for 12 months, then $99.00/month." —
 * every phase in order, deliberately, because a sentence that names only the
 * first and last phase hides the middle one. The card's headline already shows
 * the first phase and its length, so repeating it turned this into three
 * wrapped lines of red text under a price the reader had just been told.
 *
 * Only the FIRST clause is dropped, and only when there is something after it:
 * a two-phase offer keeps "then $99.00/month", and a three-phase one keeps both
 * remaining steps, so the middle phase can never go missing here either. When
 * the shape is anything this does not recognise, the whole sentence is returned
 * untouched — a disclosure is never worth truncating on a guess.
 */
export function revertClause(disclosure: string): string {
  const [, rest] = disclosure.match(/^[^,]+,\s*(then\s.+)$/i) || [];
  if (!rest) return disclosure;
  return `${rest.charAt(0).toUpperCase()}${rest.slice(1)}`;
}

export interface DealLandingPreviewProps {
  model: DealPreviewModel;
  /**
   * The two switches that decide whether this preview is something customers
   * ever see. They live ON the preview because that is what they control —
   * asking an operator to scroll to a separate form section to answer
   * "is this on the landing page?" while the answer is rendered right here
   * was the wrong place for them.
   *
   * Optional: a caller that only wants the preview passes nothing and the
   * header renders exactly as before.
   */
  landing?: {
    showOnLandingPage: boolean;
    isDefaultDeal: boolean;
    onToggleShow: () => void;
    onToggleFeatured: () => void;
  };
}

const money2 = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/**
 * The mockup's own `.pv-head .live .dot` animation — a STATIC dot whose
 * opacity dips to .4 at the keyframe's midpoint, not an expanding ring.
 * `@keyframes` cannot be expressed with Tailwind utilities alone, so it is
 * declared once here and referenced via an arbitrary `animate-[...]` value.
 */
const LiveDotStyle: React.FC = () => (
  <style>{'@keyframes dealPreviewLiveDot { 50% { opacity: .4; } }'}</style>
);

const DeviceThumb: React.FC<{ name: string; heroImageUrl: string | null; missingImage: boolean; size: number }> = ({
  name, heroImageUrl, missingImage, size,
}) => (
  <div className="relative flex-none">
    <div
      className="rounded-[11px] border border-[rgba(255,255,255,.08)] overflow-hidden"
      style={{ background: 'linear-gradient(145deg,#26262c,#131316)' }}
    >
      <ShimmerImage
        src={heroImageUrl}
        alt={name}
        width={size}
        height={size}
        objectFit="contain"
        fallbackBgColor="bg-transparent"
        fallbackIcon={<Monitor size={Math.round(size * 0.4)} className="text-[#6b6b74]" />}
        variant="dark"
      />
    </div>
    {missingImage && (
      <span
        className="absolute -right-1 -top-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-black text-[8px] font-bold grid place-items-center border-2 border-[#101013]"
        title="No hero image on file"
      >
        !
      </span>
    )}
  </div>
);


// ---------------------------------------------------------------------------
// Landing card — `.price-card` + `.dev-strip`
// ---------------------------------------------------------------------------

const LandingCardView: React.FC<{ model: DealPreviewModel }> = ({ model }) => {
  /**
   * WHICH WAY TO PAY the viewer is currently looking at, per device.
   *
   * Owned HERE rather than inside each row, because the card's headline price
   * depends on it: choosing "Rent" instead of "Finance" is choosing a different
   * monthly bill, and a big number that ignored the buttons underneath it would
   * quote a price nobody pays.
   *
   * Keyed by the device's stable key, so a selection survives the operator
   * editing an unrelated field and the whole model being rebuilt.
   */
  const [chosenBySkuKey, setChosenBySkuKey] = React.useState<Record<string, string>>({});

  /** The cheapest recurring option, or the only one — what the live card and the wizard both preselect. */
  const preselectFor = React.useCallback((device: DealPreviewDevice): DealPreviewOption | undefined => {
    const options = device.options || [];
    const monthly = options.filter((o) => o.saleMode !== 'outright' && o.saleMode !== 'included');
    if (monthly.length) return monthly.reduce((a, b) => (b.amountCents < a.amountCents ? b : a));
    return options[0];
  }, []);

  /** The option in force for each device — an explicit choice, else the preselect. */
  const chosenFor = React.useCallback((device: DealPreviewDevice): DealPreviewOption | undefined => {
    const options = device.options || [];
    const picked = options.find((o) => o.skuId === chosenBySkuKey[device.key]);
    // Falls back when the operator removes the SKU that was selected: the
    // stored id no longer matches an option, so the preselect takes over
    // rather than the row going blank.
    return picked || preselectFor(device);
  }, [chosenBySkuKey, preselectFor]);

  /**
   * THE HEADLINE, for what is currently selected. `dealCardTotals` is shared
   * with the live landing card, so this preview cannot drift from it.
   */
  const cardTotals = React.useMemo(
    () => dealCardTotals(model.priceCents, (model.devices || []).map(chosenFor)),
    [model.priceCents, model.devices, chosenFor],
  );

  /**
   * THE CARD'S SCHEDULE — the software phases plus whichever hardware option is
   * currently chosen, through the same `buildPricePhases` the subscription is
   * billed on.
   *
   * The card used to show only `cardTotals.monthlyCents`, the SETTLED price. On
   * a deal with a limited-time price that is not the number the customer is
   * charged first, and the period was disclosed nowhere on the customer-facing
   * surface at all. An introductory price a customer is not told expires is the
   * single most complained-about pattern in subscription billing.
   */
  const schedule = React.useMemo(() => {
    // The software enters as its OWN per-package lines, never collapsed to a
    // single first/settled pair: two packages promoted for different lengths
    // produce a middle phase, and collapsing first would drop it.
    const lines: PhasedLine[] = [...(model.softwareLines || [])];
    if (lines.length === 0 && model.priceCents !== null) {
      lines.push({ key: 'software', quantity: 1, standardAmountCents: model.priceCents });
    }
    for (const device of model.devices || []) {
      const option = chosenFor(device);
      // An outright purchase has no monthly side, so it is not a phased line —
      // it is money due today, which the card states separately.
      if (!option || option.saleMode === 'outright' || option.amountCents <= 0) continue;
      lines.push({
        key: option.skuId,
        quantity: 1,
        standardAmountCents: option.amountCents,
        ...(option.promoMonths && option.promoAmountCents !== option.amountCents
          ? { intro: { introPriceCents: option.promoAmountCents, introMonths: option.promoMonths } }
          : {}),
      });
    }
    const phases = buildPricePhases(lines);
    const headline = phaseHeadline(phases);
    return {
      phases,
      changes: headline.changes,
      firstCents: headline.firstCents,
      disclosure: phaseDisclosure(phases, money2),
    };
  }, [model, chosenFor]);

  /**
   * THE HEADLINE, RECONCILED — every figure on the card describing the SAME
   * basket.
   *
   * ⚠️ THE BUG THIS FIXES. `dealCardTotals` is handed the SETTLED software price
   * as its `softwareMonthlyCents`, so its `listMonthlyCents` is
   * `settledSoftware + listDevices` — a half-list figure that is only a real
   * "was" price when the software carries no discount. The card fell back to
   * `model.strikethrough.wasCents` (software packages ALONE) whenever the
   * hardware happened not to be discounted, and struck through $145.92 beside a
   * headline of $241.92: a "was" price LOWER than the price it was crossing out.
   *
   * So the "was" is assembled here from both halves at catalog list, and shown
   * only when it is genuinely higher than what is being charged.
   */
  const headline = React.useMemo(() => {
    // Software at list. `strikethrough` already carries the packages sum, and
    // already refuses to offer one when any package is unpriced — in that case
    // there is no honest list figure and the settled price stands as its own.
    const softwareListCents = model.strikethrough.show
      ? model.strikethrough.wasCents
      : cardTotals.softwareMonthlyCents;

    // The hardware currently selected, at three prices: month one, settled, and
    // catalog list. Outright and included options are not monthly and belong to
    // the "due today" line instead.
    let deviceFirstCents = 0;
    let deviceSettledCents = 0;
    let deviceListCents = 0;
    for (const device of model.devices || []) {
      const option = chosenFor(device);
      if (!option || option.saleMode === 'outright' || option.saleMode === 'included') continue;
      deviceFirstCents += option.promoMonths ? option.promoAmountCents : option.amountCents;
      deviceSettledCents += option.amountCents;
      deviceListCents += option.listAmountCents;
    }

    const settledCents = cardTotals.softwareMonthlyCents + deviceSettledCents;
    const firstCents = schedule.changes ? schedule.firstCents : settledCents;
    const wasCents = softwareListCents + deviceListCents;

    return {
      softwareListCents,
      softwareShownCents: schedule.changes ? model.softwareFirstCents : cardTotals.softwareMonthlyCents,
      deviceShownCents: schedule.changes ? deviceFirstCents : deviceSettledCents,
      deviceSettledCents,
      deviceListCents,
      settledCents,
      firstCents,
      wasCents,
      savingCents: Math.max(0, wasCents - firstCents),
      /** Never show a "was" that is not actually more than the price beside it. */
      showWas: wasCents > firstCents,
      /** How long month one's price lasts — the first phase's own length. */
      promoMonths: schedule.phases[0]?.iterations,
      unpriced: model.priceCents === null && deviceSettledCents === 0,
    };
  }, [model, cardTotals, chosenFor, schedule]);

  const missingImageCount = model.devices.filter((d) => d.missingImage).length;

  // TASK 3e — the ruby glow is the FEATURED treatment. The live landing page
  // only draws it for the default deal (`deal.isDefault`); a preview that
  // always drew it showed every deal as though it were the one that isn't.
  const featured = model.isDefaultDeal;

  return (
    <div>
      <div
        className="relative max-w-[400px] mx-auto mt-3.5 rounded-[24px] px-[30px] pt-[34px] pb-[30px] backdrop-blur-[20px]"
        style={featured ? {
          background: 'linear-gradient(180deg, rgba(220,20,60,.07), rgba(255,255,255,.035) 40%)',
          border: '1px solid rgba(220,20,60,.45)',
          boxShadow: '0 0 0 1px rgba(220,20,60,.25), 0 24px 70px -22px rgba(220,20,60,.35)',
        } : {
          background: '#101013',
          border: '1px solid rgba(255,255,255,.08)',
        }}
      >
        {featured && (
          <div
            className="absolute top-0 left-[8%] right-[8%] h-px"
            style={{ background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent)' }}
          />
        )}

        {/* TASK 3d — pill parity: "No commitment" when there is neither an
            explicit label nor a trial to show, exactly like the live card. */}
        <div
          className="absolute -top-[13px] left-7 px-[13px] py-[5px] rounded-full text-[11px] font-bold tracking-[.06em] uppercase text-white whitespace-nowrap"
          style={model.label
            ? { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})`, boxShadow: '0 4px 18px rgba(220,20,60,.35)' }
            : { background: '#3f3f46' }}
        >
          {model.label || 'No commitment'}
        </div>

        <div className="text-[21px] font-semibold text-[#f4f4f5]">{model.name || 'Untitled deal'}</div>

        {/* ───────────────────────── THE OFFER ─────────────────────────
            Read top to bottom the way a buyer reads it: what KIND of offer this
            is, then the price, then what they save, then the small print. The
            badge sits ABOVE the number rather than under it — it is the lead-in
            to the price, and beneath it read as a footnote to a figure the
            customer had already taken as permanent. */}
        <div className="mt-3.5 mb-1.5">
          {schedule.changes && (
            <div
              className="inline-flex items-center gap-1.5 px-2.5 py-[4px] rounded-full mb-2 text-[9.5px] font-extrabold tracking-[.1em] uppercase text-white"
              style={{
                background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})`,
                boxShadow: '0 3px 14px -2px rgba(220,20,60,.55)',
              }}
            >
              <Sparkles className="w-[11px] h-[11px]" strokeWidth={2.5} />
              Limited-time offer
            </div>
          )}

          <div className="flex items-baseline gap-[9px] flex-wrap">
            {/* The "was": BOTH halves at catalog list, and only when it is
                actually more than the price beside it. */}
            {headline.showWas && (
              <span className="text-[17px] text-[#6b6b74] line-through font-light tabular-nums">
                {money2(headline.wasCents)}
              </span>
            )}
            {/* THE PRICE — month one when there is a limited-time price, in
                amber whenever the deal moved it, plain card ink when it did not,
                so the colour always means "this is the saving". */}
            <span
              className="text-[46px] font-light tracking-[-.04em] tabular-nums leading-none"
              style={{ color: headline.showWas || schedule.changes ? SAVING : '#f4f4f5' }}
            >
              {headline.unpriced ? '—' : money2(headline.firstCents)}
            </span>
            <span className="text-sm text-[#6b6b74]">/ month</span>
          </div>

          {/* THE SAVING, and how long it lasts — the two facts a buyer weighs.
              Kept on one row so the promotional period is never separated from
              the number it applies to. */}
          {!headline.unpriced && (headline.savingCents > 0 || headline.promoMonths) && (
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {headline.savingCents > 0 && (
                <span
                  className="inline-block px-2 py-[3px] rounded-md text-[10px] font-extrabold tracking-[.05em] uppercase tabular-nums"
                  style={{ background: 'rgba(251,191,36,.14)', color: SAVING, border: '1px solid rgba(251,191,36,.35)' }}
                >
                  Save {money2(headline.savingCents)}/mo
                </span>
              )}
              {schedule.changes && headline.promoMonths && (
                <span className="text-[11px] text-[#a1a1aa]">
                  for the first {headline.promoMonths} month{headline.promoMonths === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* THE DISCLOSURE — small, red, and generated from the phases that will
            actually bill, so the sentence a customer reads is the schedule they
            get. It names the promotional price, its exact duration and the price
            it reverts to, which is what an introductory-offer disclosure has to
            state; it is never marketing copy an operator can edit away from the
            schedule.

            The leading "$X/month for N months" clause is TRIMMED here and only
            here: the headline above already states it in 46px, and repeating it
            is what made this paragraph three wrapped lines of red. What survives
            is the part the headline does NOT say — what it reverts to, and that
            it renews. Trimming is presentational and never touches
            `phaseDisclosure`, which the checkout and the agreement still render
            in full. */}
        {schedule.changes && schedule.disclosure && (
          <p className="mb-2.5 text-[10.5px] leading-[1.55]" style={{ color: RUBY_LIGHT }}>
            {revertClause(schedule.disclosure)} Renews automatically at the standard price until cancelled.
          </p>
        )}

        {/* THE SPLIT + WHAT IS DUE TODAY. Both move with the hardware buttons
            below; a headline that changed while the breakdown did not would be
            the same lie in a smaller font. "$0 down" is stated rather than
            hidden — it is the offer. */}
        {model.devices.length > 0 && (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 -mt-0.5 mb-1.5">
            {/* The split, each half showing its own saving: the catalog list
                struck through, the deal's figure in amber. A customer looking at
                a discounted headline can see WHICH half moved. */}
            {/* THE SPLIT, ON THE SAME BASIS AS THE HEADLINE. Both halves show
                the month-one figure whenever the headline does, so the two
                always add up to it; showing settled parts under a promotional
                total was the same lie in a smaller font — the split read
                $135.93 + $135.98 = $271.91 beneath a headline of $241.92.
                Each half carries its own catalog list struck through, so a
                customer can see WHICH one moved. */}
            <span className="font-mono text-[10.5px] text-[#6b6b74] tabular-nums">
              software{' '}
              {model.priceCents === null ? '—' : (
                <>
                  {headline.softwareListCents > headline.softwareShownCents && (
                    <span className="line-through mr-1">{money2(headline.softwareListCents)}</span>
                  )}
                  <span
                    style={headline.softwareListCents > headline.softwareShownCents ? { color: SAVING } : undefined}
                  >
                    {money2(headline.softwareShownCents)}
                  </span>
                </>
              )}
              {headline.deviceSettledCents > 0 && (
                <>
                  {' + devices '}
                  {headline.deviceListCents > headline.deviceShownCents && (
                    <span className="line-through mr-1">{money2(headline.deviceListCents)}</span>
                  )}
                  <span style={headline.deviceListCents > headline.deviceShownCents ? { color: SAVING } : undefined}>
                    {money2(headline.deviceShownCents)}
                  </span>
                </>
              )}
            </span>
            <span className="font-mono text-[10.5px] tabular-nums">
              {cardTotals.dueTodayDiscounted && (
                <span className="text-[#6b6b74] line-through mr-1.5">{money2(cardTotals.listDueTodayCents)}</span>
              )}
              {cardTotals.dueTodayCents > 0 ? (
                <span className="text-[#f4f4f5] font-bold">{money2(cardTotals.dueTodayCents)} due today</span>
              ) : (
                <span className="text-[#4ade80] font-bold">$0 down</span>
              )}
            </span>
          </div>
        )}

        <p className="text-[13.5px] leading-[1.55] text-[#a1a1aa] font-light mb-5">{model.blurb}</p>

        <ul className={`flex flex-col gap-2.5 list-none ${model.featuresAreFallback ? 'mb-2' : 'mb-[22px]'}`}>
          {model.features.map((f) => {
            // Mirrors the live card: every REAL package carries a mark — its
            // own icon and colour where set, the generic glyph in ruby where
            // not. Only the static fallback bullets go without.
            const hasIdentity = !model.featuresAreFallback;
            const a = resolveAccent(f);
            const PkgIcon = getIcon(f.icon || '');
            return (
              <li key={f.id} className="flex items-start gap-2 text-[13.5px] text-[#a1a1aa]">
                <span
                  className="flex-none w-[17px] h-[17px] mt-px rounded-full grid place-items-center text-[10px]"
                  style={{ background: 'rgba(220,20,60,.14)', color: '#fb7185' }}
                >
                  &#10003;
                </span>
                {hasIdentity && (
                  <span
                    className="flex-none w-[18px] h-[18px] rounded-[5px] grid place-items-center"
                    style={{ background: tint(a.from, 0.18), color: a.from }}
                    aria-hidden
                  >
                    <PkgIcon size={11} />
                  </span>
                )}
                {f.name}
              </li>
            );
          })}
        </ul>
        {/* TASK 3b — the live card falls back to five static bullets when no
            packages are checked, never to an empty list. Tagged here so the
            operator knows these are NOT this deal's own packages. */}
        {model.featuresAreFallback && (
          <p className="text-[11px] text-[#6b6b74] italic mb-[22px]">(fallback — no packages selected)</p>
        )}

        {model.devices.length > 0 && (
          <div className="my-0.5 mb-[22px] rounded-[15px] p-3.5 bg-black/30 border border-[rgba(255,255,255,.08)]">
            <div className="flex justify-between font-mono text-[9.5px] tracking-[.14em] uppercase text-[#6b6b74] mb-[11px]">
              <span>Hardware included</span>
              <span className="text-[#fb7185]">{model.devices.length} device{model.devices.length === 1 ? '' : 's'}</span>
            </div>
            {model.devices.map((d, i) => (
              <PreviewDeviceRow
                key={d.key || `${d.name}-${i}`}
                device={d}
                isFirst={i === 0}
                chosen={chosenFor(d)}
                onChoose={(skuId) => setChosenBySkuKey((prev) => ({ ...prev, [d.key]: skuId }))}
              />
            ))}
          </div>
        )}

        {missingImageCount > 0 && (
          <div
            className="flex gap-2 items-start rounded-[10px] mb-4 px-[13px] py-2.5 text-[11.5px] leading-[1.5]"
            style={{ background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.3)', color: '#fbbf24' }}
          >
            <span aria-hidden="true">&#9888;</span>
            <span>
              <strong>{missingImageCount} device{missingImageCount === 1 ? '' : 's'} {missingImageCount === 1 ? 'has' : 'have'} no hero image</strong> —
              the landing card shows a placeholder until a photo is added on its hardware model page.
            </span>
          </div>
        )}

        <div
          className="flex items-center justify-center w-full py-[15px] px-7 rounded-[14px] font-semibold text-[15px] text-white"
          style={{
            background: `linear-gradient(135deg, #e8365a, ${RUBY} 45%, ${RUBY_DEEP})`,
            boxShadow: '0 4px 24px rgba(220,20,60,.35), inset 0 1px 0 rgba(255,255,255,.25)',
          }}
        >
          {'Get Started'}&nbsp;&rarr;
        </div>
      </div>

      {/* TASK 3e — a deal that is not the default one never draws the ruby
          glow on the live landing page; say so, rather than leaving the
          operator to guess why this preview looks plainer than another deal's. */}
      {!featured && (
        <p className="max-w-[400px] mx-auto mt-2.5 text-[11px] text-[#6b6b74] text-center">
          Featured glow appears when this is the default deal.
        </p>
      )}

    </div>
  );
};

// ---------------------------------------------------------------------------
// Signup banner — `.deal-banner`
// ---------------------------------------------------------------------------

const SignupBannerView: React.FC<{ model: DealPreviewModel }> = ({ model }) => (
  <div>
    <div
      className="relative overflow-hidden max-w-[400px] mx-auto mt-3.5 rounded-2xl p-4"
      style={{ background: 'linear-gradient(120deg, rgba(220,20,60,.14), rgba(120,10,35,.10))', border: '1px solid rgba(220,20,60,.32)' }}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-[26px] h-[26px] flex-none rounded-lg grid place-items-center text-[13px]"
          style={{ background: 'rgba(220,20,60,.22)', color: '#fb7185' }}
        >
          &#9670;
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-[#f4f4f5] truncate">{model.name || 'Untitled deal'}</div>
          <div className="font-mono text-[10px] text-[#fb7185] tracking-[.1em]">{model.code || '—'}</div>
        </div>
      </div>

      {/*
        TASK 3a — the REAL signup screen deliberately shows neither the price
        nor the trial here: a guard test on that screen forbids payment info
        on the signup step, because payment happens at the end of the wizard,
        not on arrival. This preview used to show both, which was simply
        wrong about what a customer sees.
      */}
      <p className="mt-2.5 text-[11px] text-[#6b6b74]">
        Price and trial are shown at checkout, never on the signup screen.
      </p>

      {model.devices.length > 0 && (
        <div className="flex items-center gap-2 mt-[11px] pt-[11px]" style={{ borderTop: '1px dashed rgba(220,20,60,.22)' }}>
          {model.devices.map((d, i) => (
            <div
              key={`${d.name}-${i}`}
              className="w-[30px] h-[30px] rounded-lg grid place-items-center bg-black/35 border border-[rgba(255,255,255,.08)] overflow-hidden flex-none"
            >
              <ShimmerImage
                src={d.heroImageUrl}
                alt={d.name}
                width={30}
                height={30}
                objectFit="contain"
                fallbackBgColor="bg-transparent"
                fallbackIcon={<Monitor size={14} className="text-[#6b6b74]" />}
                variant="dark"
              />
            </div>
          ))}
          <div className="text-[11px] text-[#a1a1aa] min-w-0">
            <strong className="text-white">{model.devices.map((d) => d.name).join(' + ')}</strong> included with this deal
          </div>
        </div>
      )}
    </div>

    <p className="max-w-[400px] mx-auto mt-[22px] text-center font-mono text-[10px] tracking-[.14em] uppercase text-[#6b6b74]">
      Shown at signup when a customer arrives with this code
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// The panel — LIVE header, tabs, whichever view is active
// ---------------------------------------------------------------------------

/** A small switch sized for the preview's dark header. */
const PreviewSwitch: React.FC<{ on: boolean; label: string; onClick: () => void }> = ({ on, label, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onClick}
    className={`inline-flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
      on
        ? 'border-[rgba(220,20,60,.5)] bg-[rgba(220,20,60,.16)] text-white'
        : 'border-[rgba(255,255,255,.1)] bg-[#101013] text-[#6b6b74] hover:text-[#a1a1aa]'
    }`}
  >
    <span className={`relative inline-block h-3.5 w-6 rounded-full transition-colors ${on ? 'bg-[#DC143C]' : 'bg-[#2a2a30]'}`}>
      <span className={`absolute top-[2px] h-2.5 w-2.5 rounded-full bg-white transition-transform ${on ? 'translate-x-[13px]' : 'translate-x-[3px]'}`} />
    </span>
    {label}
  </button>
);

/**
 * ONE DEVICE, ONE PRICE AT A TIME — the operator's mirror of the live landing
 * card's `DeviceRow` (platform-app, `DealPricingCards.tsx`).
 *
 * It has to behave the same way, not merely look similar: the whole point of
 * this panel is that an operator sees what a customer will see. So the buttons
 * name the CHOICE (Finance / Rent / Buy), exactly one price shows, and the
 * cheapest recurring option is preselected — the same option the live card and
 * the signup wizard preselect.
 */
const PreviewDeviceRow: React.FC<{
  device: DealPreviewDevice;
  isFirst: boolean;
  /** CONTROLLED: the card owns the selection, because its headline depends on it. */
  chosen?: DealPreviewOption;
  onChoose: (skuId: string) => void;
}> = ({ device, isFirst, chosen, onChoose }) => {
  const options = device.options || [];
  const setChosenSkuId = onChoose;

  return (
    <div className={`flex items-start gap-[11px] py-[7px] ${isFirst ? '' : 'border-t border-dashed border-[rgba(255,255,255,.06)]'}`}>
      <DeviceThumb name={device.name} heroImageUrl={device.heroImageUrl} missingImage={device.missingImage} size={46} />
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-semibold text-[#f4f4f5] truncate">{device.name}</div>
        {device.contentsSummary && (
          <div className="text-[10.5px] text-[#6b6b74] mt-px truncate">{device.contentsSummary}</div>
        )}

        {/* Only a real choice gets a chooser — one option needs no buttons. */}
        {options.length > 1 && (
          <div className="flex flex-wrap gap-1 mt-1.5" role="group" aria-label={`How to pay for ${device.name}`}>
            {options.map((opt) => {
              const on = opt.skuId === chosen?.skuId;
              return (
                <button
                  key={opt.skuId}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setChosenSkuId(opt.skuId)}
                  className={`px-2.5 py-1 rounded-md text-[10.5px] font-bold tracking-[.02em] border transition min-h-[28px] ${
                    on
                      ? 'bg-[#dc143c] border-[#dc143c] text-white'
                      : 'bg-white/[0.05] border-white/[0.09] text-[#a1a1aa] hover:bg-white/[0.09] hover:text-white'
                  }`}
                >
                  {opt.tab}
                </button>
              );
            })}
          </div>
        )}

        {chosen && (() => {
          const promoted = Boolean(chosen.promoMonths) && chosen.promoAmountCents !== chosen.amountCents;
          return (
            <div className="mt-1.5">
              <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
                {/* The device's FULL catalog price, struck through, pointing at
                    the amber figure beside it — the same grammar the headline
                    uses, so hardware and software read as one card. */}
                {chosen.discounted && (
                  <span className="font-mono text-[10.5px] text-[#6b6b74] line-through tabular-nums">
                    {formatDollars(chosen.listAmountCents)}
                  </span>
                )}
                <span
                  className="font-mono text-[12px] font-bold whitespace-nowrap tabular-nums"
                  style={{ color: chosen.discounted || promoted ? SAVING : '#ffffff' }}
                >
                  {promoted
                    ? (chosen.promoAmountCents === 0 ? 'Free' : formatDollars(chosen.promoAmountCents))
                    : chosen.priceLabel}
                </span>
                {chosen.saleMode !== 'outright' && chosen.saleMode !== 'included' && (
                  <span className="font-mono text-[10.5px] text-[#a1a1aa] tabular-nums">
                    {chosen.downPaymentCents > 0 ? `${formatDollars(chosen.downPaymentCents)} down` : '$0 down'}
                  </span>
                )}
              </div>
              {/* The device's own limited-time period, disclosed on the row it
                  belongs to as well as in the card's headline sentence. */}
              {promoted && (
                <p className="font-mono text-[9.5px] mt-0.5 tabular-nums" style={{ color: RUBY_LIGHT }}>
                  limited-time · first {chosen.promoMonths} month{chosen.promoMonths === 1 ? '' : 's'}, then{' '}
                  {chosen.priceLabel}
                </p>
              )}
              {/* WHAT THIS PRICE COVERS. The card previews ONE unit at ONE
                  location, so an offer tiered to the first N devices or gated to
                  the first N stores looked exactly like an unlimited one. */}
              {chosen.scopeNote && (
                <p className="text-[9.5px] mt-0.5 text-[#6b6b74]">{chosen.scopeNote}</p>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const DealLandingPreview: React.FC<DealLandingPreviewProps> = ({ model, landing }) => {
  const [tab, setTab] = useState<PreviewTab>('card');

  const tabButtonClass = (active: boolean) =>
    `px-4 py-2 rounded-full text-[11.5px] font-semibold transition-colors ${active ? 'text-white' : 'text-[#6b6b74] hover:text-[#a1a1aa]'}`;

  return (
    <div
      className="rounded-2xl border border-[rgba(255,255,255,.08)] p-5 pb-8"
      style={{ background: 'radial-gradient(60% 40% at 70% 0%, rgba(220,20,60,.09), transparent 65%), #050507' }}
    >
      <LiveDotStyle />
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[.14em] text-[#6b6b74]">
          <span
            className="inline-block h-[7px] w-[7px] rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981] animate-[dealPreviewLiveDot_2s_infinite] motion-reduce:animate-none"
          />
          LIVE PREVIEW &mdash; WHAT CUSTOMERS SEE
        </span>

        {landing && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <PreviewSwitch
              on={landing.showOnLandingPage}
              label="On landing page"
              onClick={landing.onToggleShow}
            />
            {/* Featured only means anything for a deal that is actually shown. */}
            {landing.showOnLandingPage && (
              <PreviewSwitch
                on={landing.isDefaultDeal}
                label="Featured"
                onClick={landing.onToggleFeatured}
              />
            )}
          </div>
        )}
        <div className="ml-auto flex gap-0.5 bg-[#101013] border border-[rgba(255,255,255,.08)] rounded-full p-[3px]">
          <button type="button" onClick={() => setTab('card')} className={tabButtonClass(tab === 'card')}
            style={tab === 'card' ? { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` } : undefined}
          >
            Landing card
          </button>
          <button type="button" onClick={() => setTab('banner')} className={tabButtonClass(tab === 'banner')}
            style={tab === 'banner' ? { background: `linear-gradient(135deg, ${RUBY}, ${RUBY_DEEP})` } : undefined}
          >
            Signup banner
          </button>
        </div>
      </div>

      {tab === 'card' ? <LandingCardView model={model} /> : <SignupBannerView model={model} />}
    </div>
  );
};

export default DealLandingPreview;
